import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { api, ApiError } from '@/lib/api.js';

// ── LimitCorrectionDialog: the bound liability limit, corrected from the carrier's document ──
// (David 2026-11-01: a $100K per passenger typed where Mach 2's quote said $200K.)
//
// Opens on what the bind holds now (GET .../correction/limits with nothing stated), previews the change as the broker
// types, and commits with a reason and the carrier document. The server
// (services/record-correction.service.js applyLimitCorrection) moves the bind, the quote option behind it, the CRM
// and the signed application together and reads them back; this screen derives nothing.

const money = (n) => (n == null ? 'not stated' : `$${Number(n).toLocaleString('en-US')}`);
const limitWords = (l) => (!l ? '' : l.liability_smooth === true
  ? `${money(l.liability_per_occurrence)} smooth (single limit)`
  : `${money(l.liability_per_occurrence)} each occurrence / ${money(l.liability_per_passenger)} each passenger`);
const digits = (v) => String(v || '').replace(/[^0-9]/g, '');

export default function LimitCorrectionDialog({ dealId, open, onOpenChange, onSaved }) {
  const [current, setCurrent] = useState(null); // the preview with nothing stated: what the bind holds now
  const [loadError, setLoadError] = useState(null);
  const [occ, setOcc] = useState('');
  const [pax, setPax] = useState('');
  const [smooth, setSmooth] = useState('keep'); // keep | split | smooth
  const [preview, setPreview] = useState(null);
  const [documentName, setDocumentName] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const stated = () => {
    const q = {};
    if (digits(occ)) q.each_occurrence = digits(occ);
    if (smooth === 'smooth') q.smooth = 'true';
    else {
      if (smooth === 'split') q.smooth = 'false';
      if (digits(pax)) q.each_passenger = digits(pax);
    }
    return q;
  };

  useEffect(() => {
    if (!open) {
      setCurrent(null); setLoadError(null); setOcc(''); setPax(''); setSmooth('keep');
      setPreview(null); setDocumentName(''); setReason(''); setError(null);
      return;
    }
    api.get(`/api/tracon/deal/${encodeURIComponent(dealId)}/correction/limits`)
      .then((res) => setCurrent(res))
      .catch((err) => setLoadError((err instanceof ApiError ? err.body?.error : err.message) || 'Could not read this policy\'s limits.'));
  }, [open, dealId]);

  async function handlePreview() {
    setError(null);
    const q = stated();
    if (!Object.keys(q).length) { setError('Type the limit the carrier\'s document states.'); return; }
    try {
      const res = await api.get(`/api/tracon/deal/${encodeURIComponent(dealId)}/correction/limits?${new URLSearchParams(q).toString()}`);
      if (res.unchanged) { setPreview(null); setError('The bind already states this limit.'); return; }
      setPreview(res);
    } catch (err) {
      setPreview(null);
      setError((err instanceof ApiError ? err.body?.error : err.message) || 'Preview failed');
    }
  }

  async function handleCommit() {
    if (!documentName.trim()) { setError('Name the carrier document the limit is read from.'); return; }
    if (!reason.trim()) { setError('A correction records why. Add a reason.'); return; }
    setSaving(true);
    setError(null);
    try {
      const res = await api.post(`/api/tracon/deal/${encodeURIComponent(dealId)}/correction/limits`, {
        ...stated(), document: documentName.trim(), reason: reason.trim(),
      });
      const doc = res.document?.link ? ' The binding application was regenerated.' : '';
      toast.success(`Liability limit corrected to ${limitWords(res.after)}.${doc}`);
      for (const note of res.partial || []) toast.info(note);
      onOpenChange?.(false);
      if (typeof onSaved === 'function') onSaved(res);
    } catch (err) {
      const body = err instanceof ApiError ? err.body : null;
      const disagree = body?.disagreements?.length ? ` ${body.disagreements.join('; ')}` : '';
      setError((body?.error || err.message || 'Correction failed') + disagree);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Correct liability limit</DialogTitle>
          <DialogDescription>
            Fix a bound limit that was entered wrong, from the carrier&apos;s quote, binder or policy.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1 text-sm">
          {loadError ? (
            <div className="rounded-card border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">{loadError}</div>
          ) : !current ? (
            <p className="text-text-muted">Reading the bound limit…</p>
          ) : (
            <p className="text-text">
              Bound{current.carrier_name ? ` with ${current.carrier_name}` : ''} for {current.period?.effective} to {current.period?.end}:
              {' '}<span className="font-semibold">{limitWords(current.before)}</span>.
            </p>
          )}

          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-xs font-medium uppercase tracking-wide text-text-muted">Each occurrence</span>
              <Input inputMode="numeric" value={occ} onChange={(e) => { setOcc(e.target.value); setPreview(null); }} placeholder="Unchanged" className="mt-1.5" />
            </label>
            <label className="block">
              <span className="text-xs font-medium uppercase tracking-wide text-text-muted">Each passenger</span>
              <Input inputMode="numeric" value={pax} disabled={smooth === 'smooth'} onChange={(e) => { setPax(e.target.value); setPreview(null); }} placeholder={smooth === 'smooth' ? 'None (smooth)' : 'Unchanged'} className="mt-1.5" />
            </label>
          </div>
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-text-muted">Limit type</span>
            <select
              value={smooth}
              onChange={(e) => { setSmooth(e.target.value); setPreview(null); }}
              className="mt-1.5 flex min-h-[44px] w-full rounded-card border border-border bg-bg px-3 py-2 text-sm text-text focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent"
            >
              <option value="keep">Keep as bound</option>
              <option value="split">Split (each occurrence / each passenger)</option>
              <option value="smooth">Smooth (single limit, no per-passenger)</option>
            </select>
          </label>

          {preview ? (
            <div className="space-y-2 rounded-card border border-accent/40 bg-accent/10 px-3 py-2 text-xs text-text">
              <p>
                <span className="font-semibold">{limitWords(preview.before)}</span> becomes
                {' '}<span className="font-semibold">{limitWords(preview.after)}</span> on the bind
                {preview.option_id ? ', the quote it was bound from' : ''}, the CRM deal and the signed binding application.
              </p>
              {(preview.notes || []).map((n) => <p key={n} className="text-text-muted">{n}</p>)}
              <label className="block">
                <span className="text-xs font-medium uppercase tracking-wide text-text-muted">Carrier document</span>
                <Input value={documentName} onChange={(e) => setDocumentName(e.target.value)} placeholder="e.g. Mach 2 quote 021610-02, page 2" className="mt-1.5" />
              </label>
              <label className="block">
                <span className="text-xs font-medium uppercase tracking-wide text-text-muted">Reason</span>
                <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. typed $100K at quote entry" className="mt-1.5" />
              </label>
            </div>
          ) : null}

          {error ? (
            <div className="rounded-card border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">{error}</div>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange?.(false)} disabled={saving}>Cancel</Button>
          {preview ? (
            <Button onClick={handleCommit} disabled={saving || !reason.trim() || !documentName.trim()} className="bg-accent text-white hover:bg-accent/90">
              {saving ? 'Correcting…' : 'Correct the limit on this policy'}
            </Button>
          ) : (
            <Button onClick={handlePreview} disabled={!current} className="bg-accent text-white hover:bg-accent/90">
              Preview the correction
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
